import { useEffect, useState } from 'react';
import { useAudio } from '../contexts/AudioContext';
import { Button } from './ui/button';
import { Play, Pause, SkipBack, SkipForward, X, Loader2 } from 'lucide-react';
import JournalPrompt from './JournalPrompt';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const AudioPlayer = () => {
  const { currentAudio, isPlaying, isBuffering, currentTime, duration, mode, play, pause, seek, showJournalPrompt, completedAudio, closeJournalPrompt, closePlayer } = useAudio();
  const [localTime, setLocalTime] = useState(0);

  useEffect(() => {
    setLocalTime(currentTime);
  }, [currentTime]);

  if (!currentAudio) return null;

  const handleProgressClick = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    seek(newTime);
  };

  const handleSkip = (seconds) => {
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    seek(newTime);
  };

  const isMovementMode = mode === 'movement';

  return (
    <div
      className={`fixed bottom-20 left-0 right-0 mx-auto max-w-lg ${
        isMovementMode ? 'p-4' : 'p-6'
      } bg-white rounded-t-3xl shadow-[0_-8px_30px_rgb(0,0,0,0.08)] z-40`}
      data-testid="audio-player"
    >
      {/* Bouton fermer */}
      <Button
        variant="ghost"
        size="icon"
        onClick={closePlayer}
        className="absolute top-3 right-3 text-sage-400 hover:text-sage-700 hover:bg-sage-100 rounded-full"
        data-testid="audio-close-button"
      >
        <X className="w-5 h-5" />
      </Button>

      {/* Thème info */}
      <div className="mb-4 text-center">
        <h3 className="font-serif text-xl text-sage-700 font-light" data-testid="audio-player-title">
          {currentAudio.theme.title}
        </h3>
        <p className="text-sm text-sage-700/70 font-sans mt-1" data-testid="audio-player-mode">
          {isBuffering ? 'Chargement en cours...' : `Mode ${mode === 'zen' ? 'Zen' : 'Mouvement'}`}
        </p>
      </div>

      {/* Progress bar */}
      <div
        className="audio-progress mb-4 cursor-pointer"
        onClick={handleProgressClick}
        data-testid="audio-progress-bar"
      >
        <div
          className="audio-progress-fill"
          style={{ width: `${(localTime / duration) * 100}%` }}
        />
      </div>

      {/* Time */}
      <div className="flex justify-between text-sm text-sage-700/70 font-sans mb-4">
        <span data-testid="audio-current-time">{formatTime(localTime)}</span>
        <span data-testid="audio-duration">{formatTime(duration)}</span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center space-x-4">
        {!isMovementMode && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleSkip(-10)}
            className="text-sage-700"
            data-testid="audio-skip-back-button"
          >
            <SkipBack className="w-6 h-6" />
          </Button>
        )}

        <Button
          onClick={isBuffering ? undefined : (isPlaying ? pause : play)}
          disabled={isBuffering}
          className={`${
            isMovementMode
              ? 'w-24 h-24 rounded-full bg-declic hover:bg-declic/90'
              : 'w-16 h-16 rounded-full bg-sage-500 hover:bg-sage-600'
          } text-white shadow-lg`}
          data-testid="audio-play-pause-button"
        >
          {isBuffering ? (
            <Loader2 className={`${isMovementMode ? 'w-10 h-10' : 'w-7 h-7'} animate-spin`} />
          ) : isPlaying ? (
            <Pause className={isMovementMode ? 'w-12 h-12' : 'w-8 h-8'} fill="currentColor" />
          ) : (
            <Play className={isMovementMode ? 'w-12 h-12' : 'w-8 h-8'} fill="currentColor" />
          )}
        </Button>

        {!isMovementMode && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleSkip(10)}
            className="text-sage-700"
            data-testid="audio-skip-forward-button"
          >
            <SkipForward className="w-6 h-6" />
          </Button>
        )}
      </div>

      {isMovementMode && (
        <p className="text-center text-sm text-sage-700/70 font-sans mt-4">
          Marchez doucement et laissez-vous guider
        </p>
      )}

      {/* Journal prompt après écoute */}
      {showJournalPrompt && completedAudio && (
        <JournalPrompt
          themeSlug={completedAudio.theme.slug}
          mode={completedAudio.mode}
          themeTitle={completedAudio.theme.title}
          onClose={closeJournalPrompt}
          onSkip={closeJournalPrompt}
        />
      )}
    </div>
  );
};

export default AudioPlayer;